import React, { useEffect, useState, useContext, useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { SettingsContext } from '@/context/settings-context'
import { usePageData, useAdAssets } from '../hooks'
import { useFormValidation } from '../hooks/useFormValidation'
import { useAdCreativeProcessor } from '../hooks/useAdCreativeProcessor'
import { CharLimitedInput } from '@/components/common/CharLimitedInput'
import { AdPreview } from '@/components/ui/ad-preview'
import { TagsInput } from 'react-tag-input-component'
import { z } from '@/utils/myZodLite'
import { isEmpty } from 'lodash'
import { SAVE_AD_CREATIVE, CLONE_AD_CREATIVE } from '@/graphql/media-buying-queries'
import { UPDATE_AD_CREATIVE_FULL } from '@/graphql/ad-creatives-library-queries'
import { showNotification } from '@/utils/notifications'
import { Button } from '@/components/ui/button'
import { CubeLoader } from '@/components/ui/cube-loader'
import SubmitButton from '@/components/common/SubmitButton'
import WizardSteps from '@/components/common/wizard/WizardSteps'
import { useCarouselCards } from '@/components/mediaBuying/adCreatives/screens/carouselCards/hooks/useCarouselCards.js'
import { GET_AD_CREATIVE_ASSET_VARATIONS_BY_AD_CREATIVE_ID } from '@/graphql/ads/ad-assets-queries'

export const ReviewSave = () => {
    const {
        getStepContentData,
        setStepContentData,
        registerNextGuard,
        clearWizardStorage,
        registerCustomNavigation,
        clearCustomNavigation,
        campaignFormat,
        dataForStepper,
        id: creativeTempId,
        editCreativeId,
        cloneCreativeId,
        assetAdId,
    } = useWizard()
    const isEditMode = !!editCreativeId
    const isCloneMode = !!cloneCreativeId
    const { connectedPublishers } = useMediaBuyingContext()
    const { theme } = useContext(SettingsContext)
    const navigate = useNavigate()
    const { id: campaignId } = useParams()

    const { pageIcon, pageTitle } = usePageData(connectedPublishers)
    const { collectFormData } = useAdCreativeProcessor()
    const selectFormatData = getStepContentData('selectFormat') || {}
    const adCreativeFormat = selectFormatData.format

    // Form state
    const [name, setName] = useState('')
    const [tags, setTags] = useState([])
    const [assetVariations, setAssetVariations] = useState([])
    const [assetVariationsLoading, setAssetVariationsLoading] = useState(true)
    const setMediaCropData = getStepContentData('setMediaCrop') || {}
    const librarySource = getStepContentData('uploadAssets')?.source
    const assetsCreativeId = assetAdId ?? (isCloneMode ? cloneCreativeId : creativeTempId)
    const { cards } = useCarouselCards(campaignId, assetsCreativeId)
    const { assetData } = useAdAssets(connectedPublishers?.campaignId, {}, setMediaCropData?.assetId)

    // Enhance assetData with trim data from variations
    const enhancedAssetData = useMemo(() => {
        if (!assetData || !assetVariations.length) return assetData

        // Find trim data from any variation (they should all have the same trim data)
        const variationWithTrim = assetVariations.find((v) => v.adCreativeAsset?.trim_data)
        const rawTrimData = variationWithTrim?.adCreativeAsset?.trim_data

        // Convert milliseconds to seconds (same as MediaCrop screen)
        const trimData = rawTrimData
            ? {
                  start: rawTrimData.start ? rawTrimData.start / 1000 : null,
                  end: rawTrimData.end ? rawTrimData.end / 1000 : null,
              }
            : null

        return {
            ...assetData,
            trim_data: trimData || assetData.trim_data,
        }
    }, [assetData, assetVariations])

    // Enhance carousel cards with trim data
    const enhancedCards = useMemo(() => {
        if (!cards || !assetVariations.length) return cards

        return cards.map((card) => {
            // Find variation for this card's asset
            const cardVariation = assetVariations.find(
                (v) => v.ad_creative_asset_id === card.id || v.adCreativeAsset?.id === card.id,
            )

            const rawTrimData = cardVariation?.adCreativeAsset?.trim_data

            // Convert milliseconds to seconds (same as MediaCrop screen)
            const trimData = rawTrimData
                ? {
                      start: rawTrimData.start ? rawTrimData.start / 1000 : null,
                      end: rawTrimData.end ? rawTrimData.end / 1000 : null,
                  }
                : null

            return trimData
                ? {
                      ...card,
                      trim_data: trimData,
                  }
                : card
        })
    }, [cards, assetVariations])

    useQuery(GET_AD_CREATIVE_ASSET_VARATIONS_BY_AD_CREATIVE_ID, {
        variables: { adCreativeId: assetsCreativeId },
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const variations = data.AdCreativeAssetVariation.getByAdCreativeId
            setAssetVariations(variations)
            setAssetVariationsLoading(false)
        },
    })

    const onMutationCompleted = useCallback(() => {
        showNotification('Ad creative started processing...', 'success', 3000, null, null, true, true)
        clearWizardStorage()
        navigate(`/campaigns/media-buying/${campaignId}/ad-library`)
    }, [clearWizardStorage, navigate, campaignId])

    const onMutationError = useCallback(
        (error) => {
            const fallback = isEditMode
                ? 'Failed to update ad creative. Please try again.'
                : isCloneMode
                  ? 'Failed to clone ad creative. Please try again.'
                  : 'Failed to save ad creative. Please try again.'
            const errorMessage =
                error.graphQLErrors?.[0]?.message || error.networkError?.message || error.message || fallback
            showNotification(errorMessage, 'error', 5000, null, null, true, true)
        },
        [isEditMode, isCloneMode],
    )

    const [saveAdCreative, { loading: savingCreate }] = useMutation(SAVE_AD_CREATIVE, {
        onCompleted: onMutationCompleted,
        onError: onMutationError,
    })

    const [cloneAdCreative, { loading: savingClone }] = useMutation(CLONE_AD_CREATIVE, {
        onCompleted: onMutationCompleted,
        onError: onMutationError,
    })

    const [updateAdCreative, { loading: savingUpdate }] = useMutation(UPDATE_AD_CREATIVE_FULL, {
        onCompleted: onMutationCompleted,
        onError: onMutationError,
    })

    const saving = savingCreate || savingClone || savingUpdate

    const FormSchema = useMemo(
        () =>
            z.object(
                {
                    name: z.string(),
                    tags: z.array(z.string()),
                },
                (data) => {
                    const errors = []

                    // Validate name - safely convert to string and trim
                    const nameValue = String(data.name || '').trim()
                    if (isEmpty(nameValue)) {
                        errors.push({ message: 'Name is required.', path: ['name'] })
                    }

                    return errors.length ? errors : true
                },
            ),
        [],
    )

    const { formErrors, validateForm: validate, hasErrors } = useFormValidation(FormSchema)

    const validateForm = useCallback(
        (silent = false) => {
            const result = validate(
                {
                    name: name,
                    tags: tags,
                },
                silent,
            )

            return result
        },
        [validate, name, tags], // Include name and tags in dependencies
    )

    const handleSubmit = useCallback(async () => {
        const isValid = validateForm(false)

        if (isValid === true) {
            const adAccountId = Object.values(connectedPublishers)?.[0]?.ad_account || null

            if (!isEditMode && !adAccountId) {
                showNotification(
                    'Ad Account is required. Please connect an Ad Account in campaign settings.',
                    'error',
                    3000,
                    null,
                    null,
                    true,
                    true,
                )
                return false
            }

            const collectedData = collectFormData()

            const leadFormData = getStepContentData('leadForm') || {}
            const finalData = {
                ...collectedData,
                name: name,
                tags: tags,
                meta_form_id: leadFormData.meta_form_id || null,
                lead_form_id: leadFormData.meta_form_id || leadFormData.selectedForm?.id || null,
            }

            try {
                if (isEditMode) {
                    await updateAdCreative({
                        variables: {
                            id: editCreativeId,
                            input: finalData,
                            campaignId,
                        },
                    })
                } else if (isCloneMode) {
                    const hasNewAssets = assetsCreativeId === creativeTempId
                    await cloneAdCreative({
                        variables: {
                            sourceCreativeId: cloneCreativeId,
                            input: finalData,
                            adAccountId,
                            campaignId,
                            ...(hasNewAssets && {
                                creativeTempId,
                                mediaLibrarySource: librarySource || 'upload',
                            }),
                        },
                    })
                } else {
                    await saveAdCreative({
                        variables: {
                            input: finalData,
                            adAccountId,
                            campaignId,
                            mediaLibrarySource: librarySource || 'upload',
                            creativeTempId,
                        },
                    })
                }

                return true
            } catch (error) {
                console.error('Error in handleSubmit:', error)
                return false
            }
        }

        return false
    }, [
        validateForm,
        collectFormData,
        getStepContentData,
        saveAdCreative,
        cloneAdCreative,
        updateAdCreative,
        connectedPublishers,
        librarySource,
        creativeTempId,
        name,
        tags,
        isEditMode,
        editCreativeId,
        isCloneMode,
        cloneCreativeId,
        assetAdId,
        assetsCreativeId,
    ])

    // Remove the next guard since we're handling submission manually
    useEffect(() => {
        registerNextGuard(() => false) // Prevent automatic progression
    }, [registerNextGuard])

    // Load saved data - only run once on mount
    useEffect(() => {
        const saved = getStepContentData() || {}
        setName(saved.name || '')
        setTags(saved.tags || [])
    }, [])

    // Save data on changes - only when values actually change
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setStepContentData({
                name,
                tags,
            })
        }, 100) // Debounce to prevent rapid updates

        return () => clearTimeout(timeoutId)
    }, [name, tags])

    // Custom navigation with loading state
    useEffect(() => {
        const CustomNav = () => {
            const { goBack, currentStep } = useWizard()
            return (
                <div className="d-flex gap-2">
                    {currentStep > 0 && (
                        <Button
                            variant="secondary"
                            onClick={goBack}
                            disabled={saving}
                            className="tw:w-[156px] tw:!text-sm"
                        >
                            Back
                        </Button>
                    )}
                    <SubmitButton
                        spinner="left"
                        disabled={saving}
                        buttonText="Submit"
                        showSpinner={saving}
                        onClick={handleSubmit}
                    />
                </div>
            )
        }

        registerCustomNavigation(() => <CustomNav />)

        return () => {
            clearCustomNavigation()
        }
    }, [saving, registerCustomNavigation, clearCustomNavigation, handleSubmit])

    // Get preview data from collector
    const previewData = useMemo(() => {
        const selectFormatData = getStepContentData('selectFormat') || {}
        const setAdContentData = getStepContentData('setAdContent') || {}
        const uploadAssetsData = getStepContentData('uploadAssets') || {}

        return {
            format: campaignFormat.toUpperCase(),
            showLeadForm: selectFormatData.showLeadForm || false,
            primaryText: setAdContentData.primaryText || '',
            headline: setAdContentData.headline || '',
            description: setAdContentData.description || '',
            cta: setAdContentData.cta || 'Learn More',
            displayLink: setAdContentData.displayLink || '',
            destinationUrl: setAdContentData.destinationUrl || '',
            mediaUrl: uploadAssetsData?.assets?.[0]?.croppedUrl || uploadAssetsData?.assets?.[0]?.url || null,
            carouselAssets: cards?.length > 1 ? cards : [],
        }
    }, [getStepContentData, campaignFormat])

    // Enhanced asset variants with converted trim data (ms to seconds) while keeping crop data
    const getAssetVariantForFormat = (format) => {
        const variant = assetVariations.find((x) => x.format === format)
        if (!variant) return null

        // Convert trim data from milliseconds to seconds
        const rawTrimData = variant.adCreativeAsset?.trim_data
        const convertedTrimData = rawTrimData
            ? {
                  ...rawTrimData,
                  start: rawTrimData.start ? rawTrimData.start / 1000 : null,
                  end: rawTrimData.end ? rawTrimData.end / 1000 : null,
              }
            : null

        return {
            ...variant,
            adCreativeAsset: {
                ...variant.adCreativeAsset,
                trim_data: convertedTrimData,
            },
        }
    }

    if (assetVariationsLoading)
        return (
            <div className="tw:w-full tw:flex tw:items-center tw:justify-center">
                <CubeLoader size="default" fullScreen={true} />
            </div>
        )

    return (
        <div className="tw:flex tw:h-full">
            <WizardSteps adCreative={dataForStepper} currentStep="reviewSave" />
            <div className={`d-flex flex-column ${theme} campaigns-container w-100`}>
                {/* Preview Section */}
                <div className="tw:pt-6">
                    <div className="tw:flex tw:justify-start tw:px-4 tw:max-h-[500px]">
                        {/* Square Preview */}
                        <div className="tw:flex tw:flex-col tw:items-center tw:px-2 tw:max-w-[290px]">
                            <div className="tw:flex tw:items-center tw:justify-left tw:mb-2 tw:pl-8 tw:w-full">
                                <i className="bi bi-check-circle tw:text-green-500 tw:mr-2"></i>
                                <span className="tw:text-white tw:font-medium">Square</span>
                            </div>
                            <div className="tw:scale-70 tw:origin-top">
                                <AdPreview
                                    format="square"
                                    pageIcon={pageIcon}
                                    pageTitle={pageTitle}
                                    primaryText={previewData.primaryText}
                                    headline={previewData.headline}
                                    description={previewData.description}
                                    cta={previewData.cta}
                                    displayLink={previewData.displayLink}
                                    destinationUrl={previewData.destinationUrl}
                                    assetData={enhancedAssetData}
                                    carouselAssets={enhancedCards?.length > 0 ? enhancedCards : []}
                                    showInstantForm={previewData.showLeadForm}
                                    assetVariations={assetVariations}
                                    adCreativeFormat={adCreativeFormat}
                                    thumbnails={assetData?.thumbnails}
                                    cropStateFromMediaCrop={setMediaCropData?.cropState}
                                />

                                <div className="tw:flex tw:text-slate-400 tw:text-lg tw:items-center tw:justify-left tw:mt-3 tw:pl-4 tw:w-full">
                                    <p>Feeds, in-Stream Ads For Videos And Reels</p>
                                </div>
                            </div>
                        </div>

                        {/* Vertical Preview */}
                        <div className="tw:flex tw:flex-col tw:items-center tw:px-2 tw:max-w-[290px]">
                            <div className="tw:flex tw:items-center tw:justify-left tw:mb-2 tw:pl-8 tw:w-full">
                                <i className="bi bi-check-circle tw:text-green-500 tw:mr-2"></i>
                                <span className="tw:text-white tw:font-medium">Vertical</span>
                            </div>
                            <div className="tw:scale-70 tw:origin-top">
                                <AdPreview
                                    format="vertical"
                                    pageIcon={pageIcon}
                                    pageTitle={pageTitle}
                                    primaryText={previewData.primaryText}
                                    headline={previewData.headline}
                                    description={previewData.description}
                                    cta={previewData.cta}
                                    displayLink={previewData.displayLink}
                                    destinationUrl={previewData.destinationUrl}
                                    assetData={enhancedAssetData}
                                    assetVariant={getAssetVariantForFormat('vertical')}
                                    carouselAssets={enhancedCards?.length > 0 ? enhancedCards : []}
                                    showInstantForm={previewData.showLeadForm}
                                    assetVariations={assetVariations}
                                    adCreativeFormat={adCreativeFormat}
                                    cropStateFromMediaCrop={setMediaCropData?.cropState}
                                />
                                <div className="tw:flex tw:text-slate-400 tw:text-lg tw:items-center tw:justify-left tw:mt-3 tw:pl-4 tw:w-full">
                                    <p>Stories And Reels, Search Results,Apps and Sites</p>
                                </div>
                            </div>
                        </div>

                        {/* Horizontal Preview */}
                        <div className="tw:flex tw:flex-col tw:items-center tw:px-2 tw:max-w-[290px]">
                            <div className="tw:flex tw:items-center tw:justify-left tw:mb-2 tw:pl-8 tw:w-full">
                                <i className="bi bi-check-circle tw:text-green-500 tw:mr-2"></i>
                                <span className="tw:text-white tw:font-medium">Horizontal</span>
                            </div>
                            <div className="tw:scale-70 tw:origin-top">
                                <AdPreview
                                    format="horizontal"
                                    pageIcon={pageIcon}
                                    pageTitle={pageTitle}
                                    primaryText={previewData.primaryText}
                                    headline={previewData.headline}
                                    description={previewData.description}
                                    cta={previewData.cta}
                                    displayLink={previewData.displayLink}
                                    destinationUrl={previewData.destinationUrl}
                                    assetVariant={getAssetVariantForFormat('horizontal')}
                                    assetData={enhancedAssetData}
                                    carouselAssets={enhancedCards?.length > 0 ? enhancedCards : []}
                                    showInstantForm={previewData.showLeadForm}
                                    assetVariations={assetVariations}
                                    adCreativeFormat={adCreativeFormat}
                                    cropStateFromMediaCrop={setMediaCropData?.cropState}
                                />

                                <div className="tw:flex tw:text-slate-400 tw:text-lg tw:items-center tw:justify-left tw:mt-3 tw:pl-4 tw:w-full">
                                    <p>Facebook Search results</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Form Section */}
                <div className="tw:px-6 tw:pb-6">
                    {/* Name Field */}
                    <div className="tw:mb-6">
                        <label className="form-label tw:text-base tw:text-white tw:mb-2">Name</label>
                        <CharLimitedInput
                            value={name}
                            onChange={(val) => {
                                setName(val)
                                if (hasErrors) {
                                    validateForm(true)
                                }
                            }}
                            placeholder="Set name for ads library"
                            className={formErrors['name'] ? 'tw:!border-red-500' : ''}
                        />
                        {formErrors['name'] && (
                            <div className="tw:text-xs tw:text-red-500 tw:pl-1 tw:pt-1">{formErrors['name']}</div>
                        )}
                    </div>

                    {/* Tags Field */}
                    <div className="tw:mb-6">
                        <label className="form-label tw:text-base tw:text-white tw:mb-2">
                            Tags <span className="tw:text-gray-400 tw:text-sm">Optional</span>
                        </label>
                        <div className="tw:relative">
                            <TagsInput
                                value={tags}
                                onChange={setTags}
                                name="tags"
                                placeHolder="Set or add tags"
                                classNames={{
                                    input: 'tw:bg-slate-800 tw:border-slate-600 tw:text-white tw:!bg-transparent',
                                    tag: 'tw:bg-blue-600 tw:text-white tw:!bg-transparent',
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
